/**
 * AudioInitOverlay component
 *
 * Shown when AudioContext needs user interaction to start.
 * Handles the browser restriction requiring user gesture before audio playback.
 */

import React from 'react';

interface AudioInitOverlayProps {
  trackTitle?: string;
  onInit: () => void;
  isVisible: boolean;
}

export const AudioInitOverlay: React.FC<AudioInitOverlayProps> = ({
  trackTitle,
  onInit,
  isVisible,
}) => {
  if (!isVisible) return null;

  return (
    <div 
      className="absolute inset-0 bg-studio-950/95 flex flex-col items-center justify-center z-50"
      onClick={onInit}
    >
      {/* Animated sound wave */}
      <div className="flex items-end justify-center gap-1 mb-8 h-16">
        {[...Array(8)].map((_, i) => (
          <div
            key={`wave-${i}`}
            className="w-2 bg-amber-500 rounded-full animate-pulse"
            style={{
              height: `${20 + Math.random() * 40}px`,
              animationDelay: `${i * 0.1}s`,
              animationDuration: '0.8s',
            }}
          />
        ))}
      </div>

      <h2 className="font-display text-3xl font-bold text-stone-100 mb-3 text-center px-4">
        Ready to Practice
      </h2>
      
      {trackTitle && (
        <p className="text-amber-500 text-lg mb-6 text-center px-4">
          {trackTitle}
        </p>
      )}

      <p className="text-stone-400 text-center max-w-md mb-8 px-4">
        Tap below to start practicing.
        <br />
        <span className="text-stone-500 text-sm">
          (Browsers require a tap before playing audio)
        </span>
      </p>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onInit();
        }}
        className="btn-primary text-lg px-10 py-4 animate-pulse-subtle"
      >
        Start Practice →
      </button>

      {/* Touch/click hint */}
      <div className="mt-12 flex items-center gap-2 text-stone-600 text-sm">
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={1.5}
            d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" 
          />
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={1.5}
            d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
          />
        </svg>
        <span>Game will start immediately</span>
      </div>
    </div>
  );
};

export default AudioInitOverlay;
